import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { QuoteDetail } from '@techgitter/contracts';
import { generateTicket } from '../../services/quotesApi';
import { buildQuotePromotionPreview } from '../../utils/quotePromotionPreview';
import {
  buildProductSkuIndex,
  getProductFromIndex,
  useProductCatalog,
} from '../../hooks/useProductCatalog';

interface GenerateTicketDialogProps {
  open: boolean;
  onClose: () => void;
  quote: QuoteDetail;
  onGenerated: () => void;
}

const fmt = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

export const GenerateTicketDialog: React.FC<GenerateTicketDialogProps> = ({
  open,
  onClose,
  quote,
  onGenerated,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeLineItems, setIncludeLineItems] = useState(true);
  const [subject, setSubject] = useState<string>(quote.name ?? '');

  // Re-seed when reopened against a fresh quote or after a name edit.
  useEffect(() => {
    setSubject(quote.name ?? '');
    setIncludeLineItems(true);
    setError(null);
  }, [quote.id, quote.name, open]);

  // Same projection the invoice preview uses — the ticket carries the exact
  // lines a subsequent invoice would.
  // Service lines open with the RS product's catalog description, as the
  // invoice line will (sm4rt-api reads the same catalog).
  const { products } = useProductCatalog();
  const catalogIndex = useMemo(() => buildProductSkuIndex(products), [products]);
  const { lines: previewLines, gstLabel, eftSuffix, lineDisplay, total } =
    useMemo(
      () =>
        buildQuotePromotionPreview(
          quote,
          (sku) => getProductFromIndex(catalogIndex, sku)?.description,
        ),
      [quote, catalogIndex],
    );

  // Requiring ≥1 line only when the tech asked to include them keeps us from
  // sending a request the backend would reject (parity with the invoice dialog);
  // toggling off allows a deliberately-empty ticket.
  const noLinesToInclude = includeLineItems && previewLines.length === 0;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await generateTicket(quote.number, { includeLineItems, subject });
      onGenerated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate ticket from quote</DialogTitle>
      <DialogContent>
        <TextField
          label="Ticket subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={submitting}
          fullWidth
          size="small"
          helperText="Defaults to the quote name."
          sx={{ mt: 1, mb: 1.5 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={includeLineItems}
              onChange={(e) => setIncludeLineItems(e.target.checked)}
              disabled={submitting}
            />
          }
          label="Include line items from quote"
          sx={{ mb: 1 }}
        />
        {includeLineItems ? (
          previewLines.length === 0 ? (
            <Alert severity="warning">
              This quote has no line items to add. Turn off “Include line
              items” to create an empty ticket.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                These line items will be copied onto the new ticket (prices
                shown {gstLabel}{eftSuffix}):
              </Typography>
              <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                {previewLines.map((l, idx) => (
                  <ListItem key={idx} divider>
                    <ListItemText
                      primary={
                        l.quantity && l.quantity > 1
                          ? `${l.description || '(no description)'} (×${l.quantity})`
                          : l.description || '(no description)'
                      }
                      secondary={l.productCode || '(no product code)'}
                    />
                    <Typography variant="body2">
                      {fmt(lineDisplay(l))}
                    </Typography>
                  </ListItem>
                ))}
              </List>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  Total ({gstLabel}{eftSuffix}): {fmt(total)}
                </Typography>
              </Box>
            </>
          )
        ) : (
          <Alert severity="info" sx={{ mb: 1 }}>
            An empty ticket will be created (no line items).
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary">
          {includeLineItems
            ? 'The quote will be marked accepted and locked once the ticket is created (its line items are now on the ticket). '
            : 'The quote will be marked accepted and linked to the ticket, but stays editable (no line items are added). '}
          The ticket number appears here shortly after.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || noLinesToInclude}
        >
          {submitting ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Generating…
            </>
          ) : (
            'Generate Ticket'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateTicketDialog;
